// frontend/src/pages/CartPage.js

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { debounce } from 'lodash';
import { usePageLoading } from '../contexts/PageLoadingContext';
import {
    Container, Grid, Typography, Box, Paper, Button, IconButton, TextField,
    Divider, Alert, CircularProgress, useTheme, useMediaQuery
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PageAnimation from '../components/animations/PageAnimation';
import api from '../api/axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SEO from '../components/SEO';

dayjs.extend(utc);
dayjs.extend(timezone);
const torontoTimezone = 'America/Toronto';

const showPrice = process.env.REACT_APP_PRICE === 'true';

const CartPage = () => {

    const modeLabelMap = {
        case: 'Case(s)',
        piece: 'Piece(s)',
        bag: 'Bag(s)',
        tray: 'Tray(s)',
        weight: 'lb'
    };
    const navigate = useNavigate();
    const location = useLocation();
    const editingOrderId = location.state?.editingOrderId;

    const [loading, setLoading] = useState(true);
    const { setPageLoading } = usePageLoading();

    // Report loading state to App.js for footer visibility
    useEffect(() => {
        setPageLoading(loading);
        return () => setPageLoading(false);
    }, [loading, setPageLoading]);
    const [cartItems, setCartItems] = useState({});
    const [deliveryAddress, setDeliveryAddress] = useState('');
    const [mobileNumber, setMobileNumber] = useState('');
    const [additionalInfo, setAdditionalInfo] = useState('');

    const [isAddressEditable, setAddressEditable] = useState(false);
    const [isMobileEditable, setMobileEditable] = useState(false);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

    const getDefaultDate = () => {
        const now = dayjs().tz(torontoTimezone);
        return now.hour() < 8 ? now.startOf('day') : now.add(1, 'day').startOf('day');
    };

    const [deliveryDate, setDeliveryDate] = useState(getDefaultDate());

    // --- START OF EDIT: Mobile detection ---
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    // --- END OF EDIT ---


    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const todayToronto = dayjs().tz(torontoTimezone).startOf('day');

                const cartResponse = await api.get('/cart');
                const { items, deliveryDate: fetchedDeliveryDate } = cartResponse.data;
                const fetchedCartItems = (items || []).reduce((acc, cartItem) => {
                    if (cartItem.product) {
                        const key = `${cartItem.product.id}-${cartItem.mode}`;
                        acc[key] = cartItem;
                    }
                    return acc;
                }, {});
                setCartItems(fetchedCartItems);

                if (editingOrderId) {
                    const orderResponse = await api.get(`/orders/${editingOrderId}`);
                    const { delivery_address, mobile_number, additional_info, delivery_date } = orderResponse.data;
                    setDeliveryAddress(delivery_address || '');
                    setMobileNumber(mobile_number || '');
                    setAdditionalInfo(additional_info || '');

                    const savedDate = dayjs(delivery_date);
                    if (savedDate.isBefore(todayToronto)) {
                        setDeliveryDate(getDefaultDate());
                    } else {
                        setDeliveryDate(savedDate);
                    }
                    setAddressEditable(true);
                    setMobileEditable(true);

                } else {
                    const profileResponse = await api.get('/profile');
                    if (profileResponse.data) {
                        const { businessAddress, phoneNumber } = profileResponse.data;
                        setDeliveryAddress(businessAddress || '');
                        setMobileNumber(phoneNumber || '');
                        setAddressEditable(!businessAddress);
                        setMobileEditable(!phoneNumber);
                    }
                    if (fetchedDeliveryDate) {
                        const savedDate = dayjs(fetchedDeliveryDate);
                        if (savedDate.isBefore(todayToronto)) {
                            setDeliveryDate(getDefaultDate());
                        } else {
                            setDeliveryDate(savedDate);
                        }
                    }
                    if (Object.keys(fetchedCartItems).length === 0 && !submitStatus.message) {
                        navigate('/browse');
                    }
                }
            } catch (err) {
                console.error("Failed to fetch page data:", err);
                if (!editingOrderId) navigate('/browse');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [navigate, editingOrderId, submitStatus.message]);

    const saveCartToBackend = useCallback(
        debounce(async (updatedCart, date) => {
            try {
                const allItems = Object.values(updatedCart);

                const normalItems = allItems
                    .filter(item => item.product.id !== "SPECIAL_REQUEST" && !item.product.is_special)
                    .map(item => ({
                        productId: item.product.id,
                        quantity: item.quantity,
                        mode: item.mode
                    }));

                const specialItems = allItems
                    .filter(item => item.product.id === "SPECIAL_REQUEST" || item.product.is_special)
                    .map(item => ({
                        name: item.product.name,
                        quantity: item.quantity,
                        mode: item.mode
                    }));

                const payload = {
                    items: normalItems,
                    special_items: specialItems,
                    replace_special_items: true,
                    deliveryDate: date ? date.format('YYYY-MM-DD') : null
                };
                await api.post('/cart', payload);
            } catch (err) {
                console.error("Failed to save cart:", err);
            }
        }, 800),
        []
    );

    const handleQuantityChange = (productId, mode, newQuantityStr) => {
        const newQuantity = mode === 'weight' ? parseFloat(newQuantityStr) : parseInt(newQuantityStr, 10);

        setCartItems(prev => {
            const key = `${productId}-${mode}`;
            const newCartItems = { ...prev };
            if (newQuantity > 0) {
                newCartItems[key].quantity = newQuantity;
            } else {
                delete newCartItems[key];
            }
            saveCartToBackend(newCartItems, deliveryDate);
            return newCartItems;
        });
    };

    const handleEmptyCart = () => {
        setCartItems({});
        saveCartToBackend({}, deliveryDate);
    };

    const subtotal = useMemo(() => {
        return Object.values(cartItems).reduce((acc, item) => {
            const price = item.product?.modes?.[item.mode]?.price || 0;
            return acc + (price * item.quantity);
        }, 0);
    }, [cartItems]);

    const vat = 0;
    const total = subtotal + vat;

    const handlePlaceOrUpdateOrder = async () => {
        if (isSubmitting || !deliveryAddress || !mobileNumber) {
            setSubmitStatus({ type: 'error', message: 'Please fill in Delivery Address and Mobile Number.' });
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus({ type: '', message: '' });

        const orderPayload = {
            deliveryDate: deliveryDate.format('YYYY-MM-DD'),
            deliveryAddress,
            mobileNumber,
            additionalInfo,
            totalAmount: total,
        };

        try {
            let response;
            if (editingOrderId) {
                response = await api.put(`/orders/${editingOrderId}`, orderPayload);
                setSubmitStatus({ type: 'success', message: response.data.msg || 'Order updated successfully! Redirecting...' });
            } else {
                response = await api.post('/orders', orderPayload);
                setSubmitStatus({ type: 'success', message: response.data.msg || 'Order placed successfully! Redirecting...' });
            }
            setTimeout(() => navigate('/order'), 2000);
        } catch (err) {
            const errorMessage = err.response?.data?.msg || `Failed to ${editingOrderId ? 'update' : 'place'} order. Please try again.`;
            setSubmitStatus({ type: 'error', message: errorMessage });
        } finally {
            setIsSubmitting(false);
        }
    };

    const nowInToronto = dayjs().tz(torontoTimezone);
    const isSameDayDelivery = deliveryDate && deliveryDate.isSame(nowInToronto, 'day');
    const isAfterCutoff = nowInToronto.hour() >= 8; // 8 AM or later
    const showSameDayWarning = isSameDayDelivery && isAfterCutoff;

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
    }

    return (
        <PageAnimation>
            <SEO
                title="Your Cart | Matax Express"
                description="Review and checkout your wholesale grocery order. Fast delivery to Toronto and GTA businesses."
                url="https://mataxexpress.ca/cart"
                noindex={true}
            />
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 'bold' }}>
                    {editingOrderId ? 'Edit Your Order' : 'Your Cart'}
                </Typography>

                {submitStatus.message && <Alert severity={submitStatus.type || 'info'} sx={{ mb: 2 }}>{submitStatus.message}</Alert>}

                <Grid container spacing={isMobile ? 2 : 4}>
                    <Grid item xs={12} md={8}>
                        <Paper elevation={0} sx={{ p: isMobile ? 1 : 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                            {Object.keys(cartItems).length > 0 ? Object.values(cartItems).map(({ product, quantity, mode }) => {
                                const price = product.modes?.[mode]?.price || 0;

                                // --- START OF EDIT: Conditional Mobile/Desktop Layout ---
                                if (isMobile) {
                                    return (
                                        <Paper key={`${product.id}-${mode}`} variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
                                            <Grid container spacing={1} alignItems="center">
                                                {/* icon-sized image on mobile */}
                                                <Grid item>
                                                    <Box
                                                        component="img"
                                                        src={product.image_url}
                                                        alt={product.name}
                                                        sx={{
                                                            width: 40,
                                                            height: 40,
                                                            objectFit: 'cover',
                                                            borderRadius: 1.5,
                                                            display: 'block'
                                                        }}
                                                    />
                                                </Grid>

                                                {/* name & price to the right of the icon */}
                                                <Grid item xs>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '0.95rem', lineHeight: 1 }}>
                                                            {product.name}
                                                        </Typography>
                                                        {showPrice && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
                                                                {`$${price.toFixed(2)} / ${mode === 'weight' ? 'pound' : mode}`}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Grid>

                                                {/* keep quantity controls / delete on their own row (below) like original,
                                                    but visually separated from the name/icon row */}
                                                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: '8px !important' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #e0e0e0', borderRadius: '50px' }}>
                                                        <IconButton size="small" onClick={() => handleQuantityChange(product.id, mode, quantity - 1)} sx={{ p: 1 }}>
                                                            <RemoveIcon fontSize="small" />
                                                        </IconButton>
                                                        <Typography sx={{ px: 2, fontWeight: 'bold', fontSize: '1rem', display: 'flex', alignItems: 'center' }}>
                                                            {quantity} <Typography component="span" variant="caption" sx={{ ml: 0.5 }}>{modeLabelMap[mode] || mode}</Typography>
                                                        </Typography>
                                                        <IconButton size="small" onClick={() => handleQuantityChange(product.id, mode, quantity + 1)} sx={{ p: 1 }}>
                                                            <AddIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                    <IconButton color="error" onClick={() => handleQuantityChange(product.id, mode, 0)} size="small" sx={{ ml: 1 }}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    );
                                } else {
                                    // Original Desktop Layout
                                    return (
                                        <Box key={`${product.id}-${mode}`} sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 1 }}>
                                            <img src={product.image_url} alt={product.name} style={{ width: 80, height: 80, objectFit: 'contain', borderRadius: 8, marginRight: 16 }} />
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography variant="h6">{product.name}</Typography>
                                                {showPrice && (
                                                    <Typography variant="body2" color="text.secondary" >
                                                        {`$${price.toFixed(2)} / ${mode === 'weight' ? 'pound' : mode || mode}`}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #ccc', borderRadius: '20px', p: '0 8px', ml: 2 }}>
                                                <IconButton size="small" onClick={() => handleQuantityChange(product.id, mode, quantity - 1)}><RemoveIcon /></IconButton>
                                                <Typography sx={{ minWidth: '4.5em', textAlign: 'center', mx: 1, fontWeight: 'bold' }}>
                                                    {quantity} {mode}
                                                </Typography>
                                                <IconButton size="small" onClick={() => handleQuantityChange(product.id, mode, quantity + 1)}><AddIcon /></IconButton>
                                            </Box>
                                            <IconButton color="error" sx={{ ml: 1 }} onClick={() => handleQuantityChange(product.id, mode, 0)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    )
                                }
                                // --- END OF EDIT ---

                            }) : (
                                <Typography sx={{ textAlign: 'center', p: 3 }}>Your cart is empty.</Typography>
                            )}
                            <Divider sx={{ my: 2 }} />
                            <Button color="error" onClick={handleEmptyCart} disabled={Object.keys(cartItems).length === 0}>
                                Empty Cart
                            </Button>
                        </Paper>

                        <Box sx={{ mt: 4 }}>
                            <Typography variant="h6" gutterBottom>Delivery Information</Typography>
                            <Paper elevation={0} sx={{ p: 2, mt: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                                <TextField
                                    fullWidth label="Delivery Address" variant="outlined" value={deliveryAddress}
                                    onChange={(e) => setDeliveryAddress(e.target.value)} required disabled={!isAddressEditable}
                                    multiline rows={3}
                                    InputProps={{
                                        endAdornment: (
                                            <IconButton onClick={() => setAddressEditable(!isAddressEditable)} edge="end" sx={{ alignSelf: 'flex-start', mt: 1 }}><EditIcon /></IconButton>
                                        )
                                    }}
                                />
                            </Paper>
                            <Paper elevation={0} sx={{ p: 2, mt: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                                <TextField
                                    fullWidth label="Mobile Number" variant="outlined" value={mobileNumber}
                                    onChange={(e) => setMobileNumber(e.target.value)} required disabled={!isMobileEditable}
                                    InputProps={{
                                        endAdornment: (
                                            <IconButton onClick={() => setMobileEditable(!isMobileEditable)} edge="end"><EditIcon /></IconButton>
                                        )
                                    }}
                                />
                            </Paper>
                            <Paper elevation={0} sx={{ p: 2, mt: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                                <TextField fullWidth label="Additional Information (e.g., building code)" variant="outlined" multiline rows={3} value={additionalInfo} onChange={(e) => setAdditionalInfo(e.target.value)} />
                            </Paper>

                            {showSameDayWarning ? (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    Orders placed after the 8:00 AM cutoff may experience delays. We'll contact you in case of delays.
                                </Alert>
                            ) : isAfterCutoff ? null : (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    Place your order by 8:00 AM for same-day delivery.
                                </Alert>
                            )}

                            <Paper elevation={0} sx={{ p: 2, mt: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker label="Select Delivery Date" value={deliveryDate}
                                        onChange={(newDate) => {
                                            setDeliveryDate(newDate);
                                            saveCartToBackend(cartItems, newDate);
                                        }}
                                        disablePast
                                        minDate={dayjs().tz(torontoTimezone)}
                                        slotProps={{ textField: { fullWidth: true, variant: 'outlined' } }}
                                    />
                                </LocalizationProvider>
                            </Paper>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Paper elevation={2} sx={{ p: 3, borderRadius: 2, position: isMobile ? 'static' : 'sticky', top: 20 }}>
                            <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 2 }}>
                                Summary
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            {Object.values(cartItems).map(({ product, quantity, mode }) => {
                                const price = product.modes?.[mode]?.price || 0;
                                const itemTotal = price * quantity;
                                return (
                                    <Box key={`${product.id}-${mode}`} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'flex-start' }}>
                                        <Typography variant="body2" sx={{ flex: 1, mr: 1 }}>
                                            {product.name} ({quantity} {modeLabelMap[mode] || mode})
                                        </Typography>
                                        {showPrice && (
                                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                ${itemTotal.toFixed(2)}
                                            </Typography>
                                        )}
                                    </Box>
                                )
                            })}
                            {Object.keys(cartItems).length > 0 && <Divider sx={{ my: 2 }} />}

                            {showPrice && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <Typography variant="h6">Total</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>${total.toFixed(2)}</Typography>
                                </Box>
                            )}

                            <Button
                                fullWidth variant="contained" size="large" color="primary" onClick={handlePlaceOrUpdateOrder}
                                disabled={isSubmitting || Object.keys(cartItems).length === 0}
                                sx={{ py: 1.5, textTransform: 'none', fontSize: '1.1rem', bgcolor: "#75a84b" }}
                            >
                                {isSubmitting ? <CircularProgress size={26} color="inherit" /> : (editingOrderId ? 'Update Order' : 'Checkout')}
                            </Button>
                            {showPrice && (
                                <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
                                    This is an estimated total. Final prices will be confirmed after your order is placed.
                                </Typography>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </PageAnimation>
    );
};

export default CartPage;